import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import cookie from "js-cookie";
import { toast } from 'react-toastify';
// Using HTML5 date input instead of external library

export default function BookService() {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const getCookieUser = cookie.get("user");
    const userInfo = getCookieUser ? JSON.parse(getCookieUser) : null;

    const [serviceData, setServiceData] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [selectedDate, setSelectedDate] = useState("");
    const [formData, setFormData] = useState({
        name: userInfo?.firstname || "",
        email: userInfo?.email || "",
        phoneNumber: userInfo?.phoneNumber || "",
        serviceType: "",
        vehicleType: "",
        vehicleModel: "",
        vehicleNumber: "",
        notes: "",
        timeSlot: ""
    });

    // Fetch service details
    useEffect(() => {
        if (!userInfo) {
            toast.error("Please login first to book a service");
            navigate("/login/");
            return;
        }

        const fetchService = async () => {
            try {
                setLoading(true);
                const result = await axios.get(
                  `${process.env.REACT_APP_API_URL}/getSingleCar/${serviceId}`
                );
                setServiceData(result.data.body);
                // Pre-fill form with service data
                setFormData(prev => ({
                    ...prev,
                    name: userInfo?.firstname || prev.name,
                    email: userInfo?.email || prev.email
                }));
            } catch (error) {
                console.log(error);
                toast.error("Failed to load service details");
                navigate("/shop/");
            } finally {
                setLoading(false);
            }
        };

        fetchService();
    }, [serviceId, userInfo, navigate]);

    // Fetch available slots when date is selected (no longer needs serviceId)
    useEffect(() => {
        if (selectedDate) {
            fetchAvailableSlots();
        } else {
            setAvailableSlots([]);
            setFormData(prev => ({ ...prev, timeSlot: "" }));
        }
    }, [selectedDate]);

    const fetchAvailableSlots = async () => {
        if (!selectedDate) return;

        try {
            setLoadingSlots(true);
            const result = await axios.get(
              `${process.env.REACT_APP_API_URL}/bookings/available-slots?date=${selectedDate}`
            );
            setAvailableSlots(result.data.body.availableSlots || []);
            
            if (result.data.body.availableSlots.length === 0) {
                toast.info("No available slots for this date. Please select another date.");
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to load available time slots");
            setAvailableSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (e) => {
        const dateValue = e.target.value;
        setSelectedDate(dateValue);
        setFormData(prev => ({ ...prev, timeSlot: "" })); // Reset time slot when date changes
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log("=== Form Submit Started ===");
        console.log("Form data:", formData);
        console.log("Selected date:", selectedDate);
        console.log("Submitting state:", submitting);
        console.log("Service ID:", serviceId);
        console.log("User info:", userInfo);

        // Prevent double submission
        if (submitting) {
            console.log("Already submitting, ignoring");
            return;
        }

        // Validation
        if (!formData.name || !formData.email || !formData.phoneNumber) {
            toast.error("Please fill in all required fields");
            console.log("Validation failed: Missing user fields");
            return;
        }

        if (!formData.serviceType) {
            toast.error("Please select a service type");
            console.log("Validation failed: No service type selected");
            return;
        }

        if (!formData.vehicleType || !formData.vehicleModel || !formData.vehicleNumber) {
            toast.error("Please provide vehicle details");
            console.log("Validation failed: Missing vehicle fields");
            return;
        }

        if (!selectedDate) {
            toast.error("Please select a booking date");
            console.log("Validation failed: No date selected");
            return;
        }

        if (!formData.timeSlot) {
            toast.error("Please select a time slot");
            console.log("Validation failed: No time slot selected");
            return;
        }

        console.log("All validations passed, proceeding with submission");

        try {
            setSubmitting(true);
            const bookingPayload = {
                carBookingId: serviceId, // Keep for backward compatibility
                name: formData.name,
                email: formData.email,
                phoneNumber: parseInt(formData.phoneNumber),
                serviceType: formData.serviceType,
                vehicleType: formData.vehicleType,
                vehicleModel: formData.vehicleModel,
                vehicleNumber: formData.vehicleNumber,
                notes: formData.notes,
                date: new Date(selectedDate).toISOString(),
                timeSlot: formData.timeSlot,
                status: 0 // Pending
            };

            console.log("Submitting booking:", bookingPayload);
            console.log("Token:", userInfo?.token);

            const res = await axios.post(
              `${process.env.REACT_APP_API_URL}/bookingController`,
              bookingPayload,
              {
                headers: {
                  Authorization: `Bearer ${userInfo?.token}`,
                },
              }
            );

            console.log("Booking response:", res.data);

            // Check for success - backend returns success: true or error: false
            if (res.status === 201 || res.data.success === true || res.data.error === false) {
                toast.success(res.data.message || "Booking submitted successfully! We will contact you soon.");
                setTimeout(() => {
                    setSubmitting(false);
                    navigate("/MyBookingDetails");
                }, 1500);
            } else {
                toast.error(res.data.message || "Failed to create booking. Please try again.");
                setSubmitting(false);
            }
        } catch (error) {
            console.error("Booking error:", error);
            console.error("Error response:", error.response);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "Failed to create booking. Please try again.";
            toast.error(errorMessage);
            setSubmitting(false);
        }
    };

    // Get minimum date (tomorrow)
    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    if (loading && !serviceData) {
        return (
            <div className="container mt-5 py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading service details...</p>
            </div>
        );
    }

    if (!serviceData) {
        return (
            <div className="container mt-5 py-5 text-center">
                <p>Service not found. Please go back and try again.</p>
                <button onClick={() => navigate("/shop/")} className="btn btn-primary">Go to Shop</button>
            </div>
        );
    }

    return (
        <>
            <div className="container mt-5 py-5">
                <div className="row">
                    <div className="col-lg-12 mb-4">
                        <h1 className="border text-center py-3">Book Car Washing Service</h1>
                    </div>
                </div>

                <div className="row">
                    {/* Service Details Card */}
                    <div className="col-lg-4 mb-4">
                        <div className="card shadow-sm h-100">
                            <img src={serviceData?.image} className="card-img-top" alt={serviceData?.title} style={{ height: "250px", objectFit: "cover" }} />
                            <div className="card-body">
                                <h4 className="card-title">{serviceData?.title}</h4>
                                <p className="text-primary fs-5 mb-2">Washing Price: Rs. {serviceData?.price}</p>
                                <p className="card-text">{serviceData?.description}</p>
                            </div>
                        </div>
                    </div>

                    {/* Booking Form */}
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body p-4">
                                <h4 className="mb-4">Booking Information</h4>
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="row">
                                        {/* User Information */}
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name" className="form-label">Full Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="email" className="form-label">Email <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter your email"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="phoneNumber" className="form-label">Phone Number <span className="text-danger">*</span></label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                id="phoneNumber"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                placeholder="Enter 10-digit mobile number"
                                                required
                                            />
                                        </div>

                                        {/* Service Type */}
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="serviceType" className="form-label">Service Type <span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                id="serviceType"
                                                name="serviceType"
                                                value={formData.serviceType}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Service Type</option>
                                                <option value="Exterior Wash">Exterior Wash</option>
                                                <option value="Interior Cleaning">Interior Cleaning</option>
                                                <option value="Premium Detailing">Premium Detailing</option>
                                                <option value="Tire & Wheel Care">Tire & Wheel Care</option>
                                            </select>
                                        </div>

                                        {/* Vehicle Information */}
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicleType" className="form-label">Vehicle Type <span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                id="vehicleType"
                                                name="vehicleType"
                                                value={formData.vehicleType}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Vehicle Type</option>
                                                <option value="Car">Car</option>
                                                <option value="SUV">SUV</option>
                                                <option value="Bike">Bike</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicleModel" className="form-label">Vehicle Model <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="vehicleModel"
                                                name="vehicleModel"
                                                value={formData.vehicleModel}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Honda City, Maruti Swift"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicleNumber" className="form-label">Vehicle Number <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="vehicleNumber"
                                                name="vehicleNumber"
                                                value={formData.vehicleNumber}
                                                onChange={handleInputChange}
                                                placeholder="e.g., PB10-AB-1234"
                                                required
                                            />
                                        </div>

                                        {/* Date and Time Selection */}
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="date" className="form-label">Select Date <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                id="date"
                                                name="date"
                                                value={selectedDate}
                                                onChange={handleDateChange}
                                                min={getMinDate()}
                                                required
                                            />
                                            <small className="text-muted">Select a date from tomorrow onwards</small>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="timeSlot" className="form-label">Select Time Slot <span className="text-danger">*</span></label>
                                            {loadingSlots ? (
                                                <div className="form-control">
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Loading available slots...
                                                </div>
                                            ) : availableSlots.length > 0 ? (
                                                <select
                                                    className="form-control"
                                                    id="timeSlot"
                                                    name="timeSlot"
                                                    value={formData.timeSlot}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Time Slot</option>
                                                    {availableSlots.map((slot, index) => (
                                                        <option key={index} value={slot}>{slot}</option>
                                                    ))}
                                                </select>
                                            ) : selectedDate ? (
                                                <div className="form-control text-muted">
                                                    No slots available for this date
                                                </div>
                                            ) : (
                                                <div className="form-control text-muted">
                                                    Please select a date first
                                                </div>
                                            )}
                                        </div>

                                        {/* Additional Notes */}
                                        <div className="col-md-12 mb-3">
                                            <label htmlFor="notes" className="form-label">Additional Notes</label>
                                            <textarea
                                                className="form-control"
                                                id="notes"
                                                name="notes"
                                                rows="3"
                                                value={formData.notes}
                                                onChange={handleInputChange}
                                                placeholder="Any special instructions or requests..."
                                            />
                                        </div>

                                        {/* Submit Button */}
                                        <div className="col-md-12 mt-3">
                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100 py-3"
                                                disabled={submitting}
                                                onClick={(e) => {
                                                    console.log("Button clicked directly");
                                                    console.log("Form valid:", e.currentTarget.form?.checkValidity());
                                                }}
                                            >
                                                {submitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-calendar me-2"></i>
                                                        Book Service
                                                    </>
                                                )}
                                            </button>
                                            {!submitting && (
                                                <div className="mt-2 text-muted small">
                                                    <strong>Debug Info:</strong> Date: {selectedDate || "Not selected"}, 
                                                    Slot: {formData.timeSlot || "Not selected"}, 
                                                    Vehicle: {formData.vehicleType || "Not selected"}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

